import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { fetchHelpArticle } from "@/api/help";
import { GfgButton } from "@/components/ui/GfgButton";
import { HELP_ARTICLE_QUERY_PARAM } from "@/constants/help";
import type { HelpPlanGate, HelpPortableTextItem } from "@/interfaces/help";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { buildHelpArticleHref, parseHelpArticleSlug } from "@/utils/help-context-helper";
import { HelpArticleVideo } from "./HelpArticleVideo";
import { PortableTextRenderer } from "./PortableTextRenderer";
import { useDelayedFlag } from "./useDelayedFlag";

/** `error.response.status === 404` for GET /help/articles/:slug. */
function isNotFoundError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | undefined)?.response?.status === 404;
}

/** "Pro" / "Enterprise", or null for an ungated article. */
function planGateLabel(planGate: HelpPlanGate): string | null {
  if (planGate === "pro") return "Pro";
  if (planGate === "ent") return "Enterprise";
  return null;
}

export interface HelpArticleViewProps {
  /** Slug of the article to show. */
  slug: string;
  /** Label the back control returns to — the rail's "This screen" label. */
  backLabel: string;
  /** Move focus to the article on show — set when it was reached from inside the panel. */
  focusOnShow: boolean;
  onBack: () => void;
  /** Opens a different article in place, e.g. from an internal link inside this one's body. */
  onOpenArticle: (slug: string) => void;
}

/**
 * The panel pane's article view, per `.hc-back`/`.hc-art`
 * (content/design/client-experience-prototype.html). Renders in place of the screen view — same
 * pane, same panel width and position, no portal or second drawer.
 */
export function HelpArticleView({
  slug,
  backLabel,
  focusOnShow,
  onBack,
  onOpenArticle,
}: HelpArticleViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const {
    data: article,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["help", "article", slug],
    queryFn: () => fetchHelpArticle(slug),
  });

  // A fast fetch renders nothing here; only a fetch still in flight after useDelayedFlag's grace
  // period shows the skeleton.
  const showSkeleton = useDelayedFlag(isLoading);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `slug` is an intentional trigger the body doesn't read — it is what distinguishes opening a new article, so the pane must scroll to top on every slug change.
  useEffect(() => {
    rootRef.current?.closest<HTMLElement>("[data-help-pane]")?.scrollTo({ top: 0 });
  }, [slug]);

  // A plain click on an internal link opens its target in this same pane instead of following the
  // href (which would otherwise full-reload the app and lose the panel). Attached imperatively
  // rather than via a JSX onClick so this delegates to the renderer's anchors without claiming
  // click-handling ownership of a non-interactive element. It listens on the always-rendered root,
  // not the body: the body mounts only once the article loads, and nothing re-runs this effect.
  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;
    function handleClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor || !bodyRef.current?.contains(anchor) || anchor.target === "_blank") return;
      const url = new URL(anchor.href);
      if (url.pathname !== "/support") return;
      const targetSlug = parseHelpArticleSlug(url.searchParams.get(HELP_ARTICLE_QUERY_PARAM));
      if (!targetSlug) return;
      event.preventDefault();
      onOpenArticle(targetSlug);
    }
    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [onOpenArticle]);

  const notFound = isError && isNotFoundError(error);
  const showContent = !isLoading && !isError && article;
  const gateLabel = showContent ? planGateLabel(article.planGate) : null;
  const hasContent = Boolean(showContent);

  // A new article reached from inside the panel takes focus — its heading, or the view itself
  // while it loads — because the row or link that opened it has just unmounted.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `slug` is the trigger — each new article takes focus.
  useEffect(() => {
    if (focusOnShow) (headingRef.current ?? rootRef.current)?.focus();
  }, [focusOnShow, slug]);

  // Focus parked on the view while the article loaded moves up to its heading once it renders.
  useEffect(() => {
    if (focusOnShow && hasContent && document.activeElement === rootRef.current) {
      headingRef.current?.focus();
    }
  }, [focusOnShow, hasContent]);

  return (
    <div ref={rootRef} tabIndex={-1} className="outline-none">
      <button
        type="button"
        onClick={onBack}
        className="mb-[18px] inline-flex items-center gap-[5px] border-0 bg-transparent p-0 font-lato text-[13px] font-bold text-graphite transition-colors hover:text-dust-ink"
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        {backLabel}
      </button>

      {showSkeleton && (
        <div className="space-y-3" aria-hidden="true">
          <div className="h-4 w-3/4 animate-pulse rounded bg-wax" />
          <div className="h-4 w-full animate-pulse rounded bg-wax" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-wax" />
        </div>
      )}

      {notFound && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <ErrorOutlineIcon className="text-[32px] text-graphite" aria-hidden="true" />
          <p className="text-[14px] text-graphite">
            This article couldn't be found. It may have been moved or removed.
          </p>
          <GfgButton type="button" variant="outline" onClick={onBack}>
            Back to Help Center
          </GfgButton>
        </div>
      )}

      {isError && !notFound && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <ErrorOutlineIcon className="text-[32px] text-good-wine" aria-hidden="true" />
          <p className="text-[14px] text-graphite">
            {apiErrorMessage(error, "We couldn't load this article. Please try again.")}
          </p>
          <GfgButton type="button" variant="outline" onClick={() => refetch()}>
            Retry
          </GfgButton>
        </div>
      )}

      {showContent && (
        <article>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="m-0 mb-3 font-crimson font-normal text-[24px] text-rodeo-black leading-[1.2] outline-none"
          >
            {article.title}
          </h1>
          <p className="mb-1 text-[14px] text-graphite">{article.summary}</p>
          {gateLabel && (
            <p className="mb-1 text-[12px] font-bold text-graphite">{gateLabel} plan required</p>
          )}
          {/* The video leads; the written article still carries the whole answer underneath
              (search and Cora read only the text). */}
          {article.video && (
            <HelpArticleVideo key={slug} slug={slug} title={article.title} video={article.video} />
          )}
          <div ref={bodyRef}>
            <PortableTextRenderer
              value={article.body as HelpPortableTextItem[]}
              internalLinkHref={buildHelpArticleHref}
            />
          </div>
        </article>
      )}
    </div>
  );
}

export default HelpArticleView;
